import React, { useState, useEffect } from 'react';
import { FiX, FiDownload, FiCheck, FiSquare } from 'react-icons/fi';
import { userRequest } from '../utils/requestMethods';
import { generateStudentIDCard } from '../utils/idCardGenerator';
import BulkIDGenerationProgressModal from './BulkIDGenerationProgressModal';
import jsPDF from 'jspdf';

const GenerateQRCodeModal = ({ 
  isOpen, 
  onClose, 
  students, 
  schoolId, 
  classId, 
  school, 
  classInfo 
}) => {
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Progress modal states
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [stepName, setStepName] = useState('');
  const [completedSteps, setCompletedSteps] = useState([]);
  const [progressError, setProgressError] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [generatedIDCards, setGeneratedIDCards] = useState([]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedStudents([]);
      setError('');
      setSuccess('');
      setShowProgressModal(false);
      setCurrentStep(0);
      setProgress(0);
      setCompletedSteps([]);
      setProgressError('');
      setIsDownloading(false);
      setGeneratedIDCards([]);
    }
  }, [isOpen]);

  const handleStudentToggle = (studentId) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSelectAll = () => {
    if (selectedStudents.length === students.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(students.map(student => student._id));
    }
  };

  const updateProgress = (step, progressValue, stepNameValue) => {
    setCurrentStep(step);
    setProgress(progressValue);
    setStepName(stepNameValue);
  };

  const completeStep = (step) => {
    setCompletedSteps(prev => [...prev, step]);
    setProgress(100);
  };

  const handleGenerateClassIDs = async () => {
    if (selectedStudents.length === 0) {
      setError('Please select at least one student');
      return;
    }

    try {
      setIsGenerating(true);
      setError('');
      setSuccess('');
      setShowProgressModal(true);
      setCurrentStep(0);
      setProgress(0);
      setCompletedSteps([]);
      setProgressError('');

      // Step 1: Generate QR Codes
      updateProgress(0, 0, 'Generating QR codes for selected students...');
      
      // Debug logging
      console.log('School data:', {
        schoolId,
        classId,
        schoolName: school?.school_name,
        schoolLogo: school?.picture?.path,
        schoolEmail: school?.school_email,
        schoolPhone: school?.phone_number,
        classInfo: classInfo?.class_name
      });
      
      const response = await userRequest.post(
        `/children/${schoolId}/generate-qr-code/${classId}`,
        {
          studentIds: selectedStudents
        }
      );

      if (response.data.failed > 0) {
        throw new Error(`${response.data.failed} students failed to generate QR codes`);
      }

      updateProgress(0, 100, 'QR codes generated successfully');
      completeStep(0);

      // Small delay to show progress
      await new Promise(resolve => setTimeout(resolve, 500));

      // Step 2: Create ID Card Templates
      updateProgress(1, 0, 'Creating ID card templates...');
      
      const idCards = [];
      const successfulResults = response.data.results.filter(result => result.success);
      
      for (let i = 0; i < successfulResults.length; i++) {
        const result = successfulResults[i];
        const student = students.find(s => s._id === result.studentId);
        
        if (student && result.qrCodeData) {
          try {
            // Debug logging
            console.log('Generating ID card for student:', {
              name: `${student.firstname} ${student.lastname}`,
              registrationId: student.registration_id,
              class: classInfo?.class_name,
              school: school?.school_name,
              hasLogo: !!school?.picture?.path,
              logoPath: school?.picture?.path
            });
            
            // Create QR code URL
            const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=600x600&data=${encodeURIComponent(result.qrCodeData)}`;
            
            // Generate ID card
            const idCardDataUrl = await generateStudentIDCard(student, school, classInfo, qrCodeUrl);
            
            if (idCardDataUrl) {
              idCards.push({
                student,
                qrCodeData: result.qrCodeData,
                idCardDataUrl,
                registrationId: result.registrationId
              });
              console.log(`✅ ID card generated successfully for ${student.firstname} ${student.lastname}`);
            } else {
              console.log(`❌ Failed to generate ID card for ${student.firstname} ${student.lastname}`);
            }
            
            // Update progress
            const cardProgress = Math.round(((i + 1) / successfulResults.length) * 100);
            updateProgress(1, cardProgress, `Creating ID card for ${student.firstname} ${student.lastname}...`);
            
            // Small delay to prevent UI blocking
            await new Promise(resolve => setTimeout(resolve, 100));
            
          } catch (cardError) {
            console.error(`Error creating ID card for ${student.firstname}:`, cardError);
          }
        }
      }

      updateProgress(1, 100, 'ID card templates created successfully');
      completeStep(1);
      setGeneratedIDCards(idCards);

      // Small delay to show progress
      await new Promise(resolve => setTimeout(resolve, 500));

      // Step 3: Generate PDF (2 ID cards per page, portrait orientation)
      updateProgress(2, 0, 'Generating PDF document...');

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Calculate dimensions for 2 cards stacked vertically (portrait)
      const pageWidth = 210; // A4 portrait width
      const pageHeight = 297; // A4 portrait height
      const margin = 8; // Margin from page edges
      const gap = 6; // Gap between cards

      // Each card dimensions (2 cards stacked vertically)
      const cardWidth = pageWidth - (margin * 2);
      const cardHeight = (pageHeight - (margin * 3) - gap) / 2; // 3 margins (top, middle, bottom) + gap

      // Add ID cards to PDF (2 per page, stacked vertically)
      for (let i = 0; i < idCards.length; i++) {
        const idCard = idCards[i];
        const isFirstCardOnPage = i % 2 === 0;

        // Add new page for every 2 cards (or first card)
        if (isFirstCardOnPage) {
          if (i > 0) {
            pdf.addPage();
          }
        }

        // Position cards: top card (i % 2 === 0) or bottom card (i % 2 === 1)
        const xPosition = margin;
        const yPosition = isFirstCardOnPage ? margin : (margin + cardHeight + gap);

        pdf.addImage(
          idCard.idCardDataUrl,
          'PNG',
          xPosition,
          yPosition,
          cardWidth,
          cardHeight
        );

        // Update progress
        const pdfProgress = Math.round(((i + 1) / idCards.length) * 100);
        updateProgress(2, pdfProgress, `Adding ${idCard.student.firstname} ${idCard.student.lastname} to PDF...`);

        // Small delay to prevent UI blocking
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      updateProgress(2, 100, 'PDF generated successfully');
      completeStep(2);

      // Small delay to show progress
      await new Promise(resolve => setTimeout(resolve, 500));

      // Step 4: Ready for Download
      updateProgress(3, 100, 'Class ID cards ready for download');
      completeStep(3);
      
      const totalPages = Math.ceil(idCards.length / 2);
      setSuccess(`Successfully generated ${idCards.length} student ID cards (${totalPages} pages)!`);
      
    } catch (error) {
      console.error('Error generating class ID cards:', error);
      setProgressError(error.message || 'Failed to generate class ID cards. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (generatedIDCards.length === 0) return;

    try {
      setIsDownloading(true);

      // Generate PDF (2 ID cards per page, portrait orientation)
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Calculate dimensions for 2 cards stacked vertically (portrait)
      const pageWidth = 210; // A4 portrait width
      const pageHeight = 297; // A4 portrait height
      const margin = 8; // Margin from page edges
      const gap = 6; // Gap between cards

      // Each card dimensions (2 cards stacked vertically)
      const cardWidth = pageWidth - (margin * 2);
      const cardHeight = (pageHeight - (margin * 3) - gap) / 2; // 3 margins (top, middle, bottom) + gap

      // Add ID cards to PDF (2 per page, stacked vertically)
      for (let i = 0; i < generatedIDCards.length; i++) {
        const idCard = generatedIDCards[i];
        const isFirstCardOnPage = i % 2 === 0;

        // Add new page for every 2 cards (or first card)
        if (isFirstCardOnPage) {
          if (i > 0) {
            pdf.addPage();
          }
        }

        // Position cards: top card (i % 2 === 0) or bottom card (i % 2 === 1)
        const xPosition = margin;
        const yPosition = isFirstCardOnPage ? margin : (margin + cardHeight + gap);

        pdf.addImage(
          idCard.idCardDataUrl,
          'PNG',
          xPosition,
          yPosition,
          cardWidth,
          cardHeight
        );
      }

      // Download PDF
      const fileName = `${school?.school_name?.replace(/[^a-zA-Z0-9]/g, '_') || 'school'}_${classInfo?.class_name?.replace(/[^a-zA-Z0-9]/g, '_') || 'class'}_ID_Cards_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      
    } catch (error) {
      console.error('Error downloading PDF:', error);
      setProgressError('Failed to download PDF. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleRetry = () => {
    setProgressError('');
    setCompletedSteps([]);
    setCurrentStep(0);
    setProgress(0);
    handleGenerateClassIDs();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold text-gray-800">Generate Class ID Cards</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Generate professional ID cards for students with QR codes for attendance
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 p-2"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
            <div className="space-y-6">
              {/* Student Selection */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-800">Select Students</h3>
                  <button
                    onClick={handleSelectAll}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    {selectedStudents.length === students.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-60 overflow-y-auto border rounded-lg p-4">
                  {students.map(student => (
                    <div
                      key={student._id}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedStudents.includes(student._id)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => handleStudentToggle(student._id)}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-4 h-4 border-2 rounded ${
                          selectedStudents.includes(student._id)
                            ? 'border-blue-500 bg-blue-500'
                            : 'border-gray-300'
                        }`}>
                          {selectedStudents.includes(student._id) && (
                            <FiCheck className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {student.firstname} {student.lastname}
                          </p>
                          <p className="text-xs text-gray-500">
                            {student.registration_id} • {student.gender}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="text-sm text-gray-600">
                  Selected: {selectedStudents.length} of {students.length} students
                </div>
              </div>

              {/* Generate Button */}
              <div className="flex justify-center">
                <button
                  onClick={handleGenerateClassIDs}
                  disabled={isGenerating || selectedStudents.length === 0}
                  className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-xl text-white bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isGenerating ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-3"></div>
                  ) : (
                    <FiSquare className="w-4 h-4 mr-3" />
                  )}
                  <span className="font-semibold">
                    {isGenerating ? "Generating ID Cards..." : `Generate Class ID Cards (${selectedStudents.length})`}
                  </span>
                </button>
              </div>
            </div>
          </div>

          {/* Notifications */}
          {success && (
            <div className="p-4 bg-green-50 border-t border-green-200">
              <div className="text-center">
                <p className="text-sm text-green-800">{success}</p>
              </div>
            </div>
          )}
          {error && (
            <div className="p-4 bg-red-50 border-t border-red-200">
              <div className="text-center">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Progress Modal */}
      <BulkIDGenerationProgressModal
        isOpen={showProgressModal}
        onClose={() => setShowProgressModal(false)}
        currentStep={currentStep}
        totalSteps={4}
        stepName={stepName}
        progress={progress}
        completedSteps={completedSteps}
        error={progressError}
        onDownloadPDF={handleDownloadPDF}
        onRetry={handleRetry}
        isDownloading={isDownloading}
      />
    </>
  );
};

export default GenerateQRCodeModal;
